// Cancellations — the photo pipeline (message 16 §6, 17 item l, 18 §3).
//
// The photo is DECOUPLED from the record: the moment the picture is taken we
// (1) downscale it on the device (1600 px long edge, JPEG 0.8 — the server
// strips EXIF again and enforces `photo_max_kb`), (2) reserve a key with
// `POST /co/photos/init`, (3) park the blob in IndexedDB under that key, and
// (4) PUT it in the background with up to three retries. The record call
// carries `photo_key`, never bytes. If the bytes never arrive the record
// stands with `photo_status: pending`; the blob stays here and is retried
// whenever the form or «إدخالاتي» opens.
//
// Honest states (incident 2026-10-21, Al-Safa: an iPhone private tab refused
// the blob, the old code swallowed that, found nothing parked and answered
// «stored» without one PUT). A photo is `stored` only after a 2xx on its PUT
// in this tab, or when the server says so. A blob IndexedDB refuses is
// uploaded from memory while the screen stays open (up to three PUTs); a key
// whose bytes are nowhere — not parked, not in memory — is `lost`, and
// nothing is sent.
//
// Review of 68f8a4b: one PUT per key at a time in this tab (a second caller
// joins the running one); the parked row is changed only by read-and-write in
// ONE transaction, so an upload's attempt count never undoes the record id a
// save attached meanwhile; a parked row carries `created_by` and is retried
// only by that user — on a shared till another account's photo waits for its
// owner instead of being dropped on the 404 the PUT gives anyone else.
import api from "@/lib/api";

const DB_NAME = "co_photos";
const STORE = "pending";
const MAX_ATTEMPTS = 3;
const PUT_TIMEOUT_MS = 30000;
const MEMORY_RETRY_MS = [2000, 5000];   // waits before the 2nd and 3rd in-memory PUT
// a row parked before rows carried `created_by` (owner unknown): kept on a 404 — it may be
// another account's — until it is this old
const UNOWNED_MAX_AGE_MS = 30 * 24 * 3600 * 1000;
export const PHOTO_EVENT = "co-photo-change";

function openDb() {
    return new Promise((resolve, reject) => {
        if (!globalThis.indexedDB) return reject(new Error("no indexedDB"));
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "photo_key" });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function tx(mode, fn) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const store = t.objectStore(STORE);
        let out;
        try { out = fn(store); } catch (e) { reject(e); }
        t.oncomplete = () => { db.close(); resolve(out && "result" in out ? out.result : out); };
        t.onerror = () => { db.close(); reject(t.error); };
        // a full disk aborts at commit time with no request error: without this the promise never settles
        t.onabort = () => { db.close(); reject(t.error || new Error("transaction aborted")); };
    });
}

// What THIS tab knows about the keys it sent: { state, record_id } with state
// uploading | waiting (parked, retried on the next open) | stored | failed.
// A blob IndexedDB refused lives only in its uploader, so this is the one
// place that knows its PUT is still running («إدخالاتي», the check after a save).
const session = new Map();

function note(photoKey, patch) {
    const cur = session.get(photoKey) || { state: null, record_id: null };
    const next = { ...cur, ...patch };
    session.set(photoKey, next);
    if (patch.state && patch.state !== cur.state) {
        try {
            window.dispatchEvent(new CustomEvent(PHOTO_EVENT, {
                detail: { photo_key: photoKey, state: next.state, record_id: next.record_id } }));
        } catch { /* no CustomEvent: nothing listens either */ }
    }
}

/** Park a blob under its key. True only when IndexedDB really holds it (read
 *  back); false when the browser refuses — an iPhone private tab, a full disk —
 *  and the caller must then upload it from memory. Never a silent swallow. */
export async function queuePhoto(entry) {
    try {
        await tx("readwrite", (s) => s.put({ attempts: 0, created_at: Date.now(), ...entry }));
        if (!entry.blob) return true;
        const back = await tx("readonly", (s) => s.get(entry.photo_key));
        if (back?.blob) return true;
        console.warn("co photo: IndexedDB did not keep the photo", entry.photo_key);
    } catch (e) {
        console.warn("co photo: IndexedDB refused the photo", entry.photo_key, e?.name || e);
    }
    return false;
}

export async function getPending(photoKey) {
    try { return await tx("readonly", (s) => s.get(photoKey)); } catch { return null; }
}

export async function listPending() {
    try { return (await tx("readonly", (s) => s.getAll())) || []; } catch { return []; }
}

export async function dropPending(photoKey) {
    try { await tx("readwrite", (s) => s.delete(photoKey)); } catch { /* ignore */ }
}

/** Merge `patch` into the parked row, read and written in ONE readwrite
 *  transaction (IndexedDB runs those one after another): the save's record id
 *  and an upload's attempt count never undo each other. True when a row was
 *  there. */
async function patchPending(photoKey, patch) {
    try {
        return !!(await tx("readwrite", (s) => {
            const req = s.get(photoKey);
            req.onsuccess = () => { if (req.result) s.put({ ...req.result, ...patch }); };
            return req;
        }));
    } catch {
        return false;   // no database here (queuePhoto already said so): nothing parked to update
    }
}

export async function attachPendingRecord(photoKey, recordId) {
    if (session.has(photoKey)) note(photoKey, { record_id: recordId });
    await patchPending(photoKey, { record_id: recordId });
}

/** 1600 px long edge, JPEG 0.8, orientation applied, EXIF dropped by re-encode. */
export async function compressImage(file, { maxEdge = 1600, quality = 0.8 } = {}) {
    let bitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
        bitmap = await createImageBitmap(file);
    }
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) throw new Error("encode failed");
    return blob;
}

/** The client gave up on a key: the server marks it `failed` (pending rows
 *  only) and keeps `reason` — idb_unavailable | upload_failed | rejected. */
async function markFailed(photoKey, reason) {
    try { await api.post(`/co/photos/${photoKey}/failed`, { reason }, { timeout: 10000 }); } catch { /* best effort */ }
}

// the running PUT of each key in this tab: a second caller (the «إدخالاتي» retry while the
// capture-time PUT is still out) joins it instead of sending the bytes twice
const inflight = new Map();

/**
 * One PUT attempt. Returns "stored" | "rejected" | "failed" | "retry" | "lost".
 *  - the bytes are the parked row's, else `blob` (the in-memory copy); with
 *    neither there is nothing to send: "lost", no PUT — never "stored";
 *  - 2xx → stored (parked blob dropped);
 *  - a parked row and an answer that is not about its bytes — 401 (the
 *    session ended), 403 (this account may not write), or 404 when `owner` is
 *    false (the row is not known to be the signed-in user's, and the PUT 404s
 *    everyone but the key's owner — message 18 §3) → "retry": the row stays,
 *    no attempt counted, nothing marked failed;
 *  - 4xx (not 429) → the bytes will never be accepted → `/failed` (reason
 *    `rejected`), blob dropped, "rejected" (the caller shows the quiet notice
 *    once — message 29 §1.4);
 *  - anything else (timeout, 5xx, 503 storage) → "retry" while attempts < 3,
 *    then `/failed` and "failed". A parked row counts its own attempts; an
 *    in-memory copy passes `attempts` (the PUTs already made).
 * A key whose PUT is already running in this tab is not sent again: the
 * caller gets that PUT's result.
 */
export function uploadPhoto(photoKey, opts = {}) {
    const running = inflight.get(photoKey);
    if (running) return running;
    const p = putOnce(photoKey, opts).finally(() => {
        if (inflight.get(photoKey) === p) inflight.delete(photoKey);
    });
    inflight.set(photoKey, p);
    return p;
}

async function putOnce(photoKey, { blob = null, attempts: memAttempts = 0, reason = null, owner = true } = {}) {
    const row = await getPending(photoKey);
    const bytes = row?.blob || blob;
    if (!bytes) return "lost";
    note(photoKey, { state: "uploading" });
    try {
        await api.put(`/co/photos/${photoKey}`, bytes, {
            headers: { "Content-Type": "image/jpeg" }, timeout: PUT_TIMEOUT_MS,
        });
        note(photoKey, { state: "stored" });
        if (row) await dropPending(photoKey);
        return "stored";
    } catch (e) {
        const status = e?.response?.status;
        if (row && (status === 401 || status === 403 || (status === 404 && !owner))) {
            if (status === 404 && !row.created_by && Date.now() - (row.created_at || 0) > UNOWNED_MAX_AGE_MS) {
                await dropPending(photoKey);
                note(photoKey, { state: "failed" });
                return "failed";
            }
            note(photoKey, { state: "waiting" });
            return "retry";
        }
        // the count as IndexedDB holds it now (another tab may have tried meanwhile)
        const fresh = row ? await getPending(photoKey) : null;
        const attempts = (row ? (fresh || row).attempts || 0 : memAttempts) + 1;
        const rejected = !!status && status < 500 && status !== 429;
        if (rejected || attempts >= MAX_ATTEMPTS) {
            if (row) await dropPending(photoKey);
            note(photoKey, { state: "failed" });
            await markFailed(photoKey, rejected ? "rejected" : (reason || (row ? "upload_failed" : "idb_unavailable")));
            return rejected ? "rejected" : "failed";
        }
        // only the count changes: the record id attached while the PUT ran stays
        if (row) await patchPending(photoKey, { attempts });
        note(photoKey, { state: row ? "waiting" : "uploading" });
        return "retry";
    }
}

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * The capture-time upload. A parked blob gets one PUT now and, on a miss,
 * stays parked for the next open (→ "pending"). A blob IndexedDB refused gets
 * up to three PUTs from memory while the screen is open, then `/failed`.
 * Resolves to "stored" | "pending" | "rejected" | "failed" | "lost".
 */
export async function sendPhoto(photoKey, blob, { parked }) {
    if (parked) {
        const res = await uploadPhoto(photoKey, { blob });
        return res === "retry" ? "pending" : res;
    }
    let res = "retry";
    for (let attempts = 0; res === "retry"; attempts += 1) {
        if (attempts) await sleep(MEMORY_RETRY_MS[attempts - 1] || 5000);
        res = await uploadPhoto(photoKey, { blob, attempts });
    }
    return res;
}

/** This tab's own view of a key: "stored" (its PUT got a 2xx here),
 *  "uploading" (a PUT is running), "parked" (in IndexedDB) or null. */
export async function localPhotoState(photoKey) {
    const s = session.get(photoKey);
    if (s?.state === "stored" || s?.state === "uploading") return s.state;
    return (await getPending(photoKey))?.blob ? "parked" : null;
}

/** The record a key of this tab was saved with (attachPendingRecord), or null. */
export function photoRecordId(photoKey) {
    return session.get(photoKey)?.record_id || null;
}

// what this tab / device knows of a record's photo, strongest first: a PUT running, the bytes
// parked here, a 2xx seen in this tab (a list read before that 2xx still says pending)
const LOCAL_RANK = { uploading: 3, parked: 2, stored: 1 };

async function localByRecord() {
    const out = {};   // record_id -> { state, key }
    const take = (rid, state, key) => {
        if (rid && (!out[rid] || LOCAL_RANK[state] > LOCAL_RANK[out[rid].state])) out[rid] = { state, key };
    };
    // the record id is the row's or, when only this tab knows it so far, the session's
    for (const row of await listPending()) {
        if (row.blob) take(row.record_id || session.get(row.photo_key)?.record_id, "parked", row.photo_key);
    }
    for (const [key, s] of session) {
        if (s.state === "uploading" || s.state === "stored") take(s.record_id, s.state, key);
    }
    return out;
}

/** {record_id: "uploading" | "parked" | "stored"} for the records whose photo
 *  this device is still sending, or saw stored in this tab; a record missing
 *  here has nothing left to send. */
export async function localPhotoStates() {
    const out = {};
    for (const [rid, v] of Object.entries(await localByRecord())) out[rid] = v.state;
    return out;
}

/** The key this device knows for a record (its PUT running, parked, or stored
 *  in this tab), or null. */
export async function localKeyForRecord(recordId) {
    return (await localByRecord())[recordId]?.key || null;
}

/**
 * The honest state of a key this form did not just upload (a draft, an edit):
 * "stored" (the server has it), "uploading" / "pending" (its bytes are still
 * on their way from here), "lost" (the server still waits and the bytes are
 * nowhere on this device), "gone" (the server no longer knows the key) or
 * "unknown" (the status call got no answer — offline).
 */
export async function resolvePhotoState(photoKey) {
    const local = await localPhotoState(photoKey);
    if (local === "stored") return "stored";
    let server = null;
    try {
        server = (await api.get(`/co/photos/${photoKey}/status`, { timeout: 10000 })).data;
    } catch (e) {
        if (e?.response?.status === 404) return "gone";
    }
    if (server?.uploaded) return "stored";
    if (local === "uploading") return "uploading";
    if (local === "parked") return "pending";
    return server ? "lost" : "unknown";
}

/** Retry the signed-in user's parked blobs, oldest first («إدخالاتي» open).
 *  A row another account parked on this device (a shared till) is left for
 *  its owner — its PUT would only 404 here; a row with no `created_by` (parked
 *  before rows were stamped) is tried, but a 404 does not drop it. */
export async function retryPendingPhotos(onChange, { userId = null } = {}) {
    const rows = (await listPending())
        .filter((r) => !r.created_by || (!!userId && r.created_by === userId))
        .sort((a, b) => (a.created_at || 0) - (b.created_at || 0));
    for (const row of rows) {
        const res = await uploadPhoto(row.photo_key, { owner: !!userId && row.created_by === userId });
        onChange && onChange(row.photo_key, res, row.record_id);
    }
    return rows.length;
}
